/**
 * Turning "click Assign on a resource card" into an assignment payload.
 *
 * <p>
 * The write endpoints are replaces, so assigning one resource has to send back
 * the whole set — otherwise picking a vehicle would wipe the driver already on
 * the job. Everything here is pure so the merge rules can be checked without a
 * browser.
 * </p>
 */
import type { AssignmentSlotValue, OrderTaskResourceInput } from '@my/tms-api';
import type { ResourceCardVM } from './planResources';

/**
 * The minimum an existing assignment needs to expose to be merged.
 *
 * <p>
 * Structural rather than one of the concrete DTOs so both shapes work: an order
 * movement reads full `OrderTaskResource` rows, while a load movement already
 * carries `LoadResourceSummary` entries on its `LoadDto`. Neither needs
 * converting first.
 * </p>
 */
export interface ExistingAssignment {
  slot: AssignmentSlotValue;
  slotIndex: number;
  isSubcontractor: boolean;
  resourceId?: string | null;
  subcontractorPartyId?: string | null;
  subcontractorTrailerId?: string | null;
  externalReference?: string | null;
  referenceSnapshot?: string | null;
  orderTaskCargoItemId?: string | null;
}

/** What a resource card resolves to when assigned. */
export interface CardAssignment {
  slot: AssignmentSlotValue;
  isSubcontractor: boolean;
  resourceId: string | null;
  subcontractorPartyId: string | null;
  subcontractorTrailerId: string | null;
  referenceSnapshot: string | null;
  /**
   * Apply only when the slot is empty.
   *
   * <p>
   * Set on a driver's linked vehicle: it should come along when nothing else is
   * in the vehicle slot, but must not overwrite a vehicle the planner chose
   * deliberately. Losing an explicit pick silently is worse than leaving a driver
   * in someone else's truck, which is at least visible on the row.
   * </p>
   */
  onlyIfEmpty?: boolean;
}

/**
 * Which slot a card fills, and whether it's ours or a subcontractor's.
 *
 * <p>
 * Returns null for cards that can't be assigned on their own — the Driver Roster
 * and Drivers pools resolve to a Driver slot, fleet cards to Vehicle or Trailer,
 * a subcontractor party to a subcontracted Vehicle (hiring a haulier means their
 * truck and driver), and a subcontractor's trailer to a subcontracted Trailer.
 * </p>
 */
/**
 * Everything a card assigns — usually one slot, but a driver brings their linked
 * vehicle along.
 *
 * <p>
 * `Resource.defaultVehicleResourceId` is the vehicle a driver normally takes out,
 * so assigning the driver without it would leave the job half-planned and the
 * planner clicking twice for the obvious case. The vehicle is marked
 * `onlyIfEmpty`, so it fills a gap rather than overriding a deliberate choice.
 * </p>
 */
export function cardAssignments(card: ResourceCardVM): CardAssignment[] {
  const primary = cardAssignment(card);
  if (!primary) return [];

  if (primary.slot === 'Driver' && card.linkedVehicle) {
    return [
      primary,
      {
        slot: 'Vehicle',
        isSubcontractor: false,
        resourceId: card.linkedVehicle.resourceId,
        subcontractorPartyId: null,
        subcontractorTrailerId: null,
        referenceSnapshot: card.linkedVehicle.reg,
        onlyIfEmpty: true,
      },
    ];
  }

  return [primary];
}

export function cardAssignment(card: ResourceCardVM): CardAssignment | null {
  // A subcontractor's trailer: the unit is registered, and the supplying haulier
  // rides along on the card so the row can name both.
  if (card.isSubcontractorUnit) {
    return {
      slot: 'Trailer',
      isSubcontractor: true,
      resourceId: null,
      subcontractorPartyId: card.subcontractorPartyId,
      subcontractorTrailerId: card.id,
      referenceSnapshot: card.title,
    };
  }

  // A subcontractor party — no resourceType, since it isn't one of our resources.
  // Hiring a haulier is hiring their vehicle (and whoever drives it).
  if (card.resourceType === null) {
    return {
      slot: 'Vehicle',
      isSubcontractor: true,
      resourceId: null,
      subcontractorPartyId: card.id,
      subcontractorTrailerId: null,
      referenceSnapshot: card.title,
    };
  }

  const slot: AssignmentSlotValue | null =
    card.resourceType === 'Driver'
      ? 'Driver'
      : card.resourceType === 'Truck' || card.resourceType === 'Rigid'
        ? 'Vehicle'
        : card.resourceType === 'Trailer'
          ? 'Trailer'
          : null;

  if (slot === null) return null;

  return {
    slot,
    isSubcontractor: false,
    resourceId: card.id,
    subcontractorPartyId: null,
    subcontractorTrailerId: null,
    referenceSnapshot: card.title,
  };
}

/** Existing row → replace-payload item, dropping the server-owned fields. */
function toInput(row: ExistingAssignment): OrderTaskResourceInput {
  return {
    slot: row.slot,
    slotIndex: row.slotIndex,
    isSubcontractor: row.isSubcontractor,
    resourceId: row.resourceId ?? null,
    subcontractorPartyId: row.subcontractorPartyId ?? null,
    subcontractorTrailerId: row.subcontractorTrailerId ?? null,
    externalReference: row.externalReference ?? null,
    referenceSnapshot: row.referenceSnapshot ?? null,
  };
}

/**
 * True when any of these assignments is scoped to a single cargo item — i.e. the
 * work has been split.
 *
 * <p>
 * The order- and load-scoped endpoints reject cargo-item references, so merging
 * at that scope would silently discard a split. Callers must check this and send
 * the operator to the per-task surface instead of quietly flattening their work.
 * </p>
 */
export const hasSplitAssignments = (current: readonly ExistingAssignment[]) =>
  current.some((r) => r.orderTaskCargoItemId != null);

/**
 * Fold a card's assignment into the set already on the job, replacing whatever
 * held the same slot and leaving the other slots alone.
 *
 * <p>
 * Slot 1 only: re-assigning the vehicle swaps the vehicle, it doesn't add a
 * second one. Filling slot 2 (a drawbar's other trailer) needs an explicit
 * surface, which doesn't exist yet.
 * </p>
 */
export function mergeAssignment(
  current: readonly ExistingAssignment[],
  next: CardAssignment,
): OrderTaskResourceInput[] {
  return mergeAssignments(current, [next]);
}

/**
 * Fold several assignments in at once — a driver plus their linked vehicle.
 *
 * <p>
 * Applied in order, so an `onlyIfEmpty` entry sees the effect of the ones before
 * it. Returns which slots actually changed alongside the payload, so the caller
 * can tell the operator what happened rather than claiming a vehicle was assigned
 * when it was skipped.
 * </p>
 */
export function mergeAssignments(
  current: readonly ExistingAssignment[],
  nexts: readonly CardAssignment[],
): OrderTaskResourceInput[] {
  return mergeAssignmentsDetailed(current, nexts).items;
}

export function mergeAssignmentsDetailed(
  current: readonly ExistingAssignment[],
  nexts: readonly CardAssignment[],
): { items: OrderTaskResourceInput[]; applied: CardAssignment[]; skipped: CardAssignment[] } {
  // Dedupe first: an order- or load-scoped read returns the same logical
  // assignment once per task, and sending them all back would post duplicate
  // slots and be rejected.
  const kept = new Map<string, OrderTaskResourceInput>();
  for (const row of current) {
    // Cargo-item-scoped rows are skipped, not folded in. They can't be expressed
    // at this scope (the order/load endpoints reject cargo-item references), and
    // keying them by slot would let a split silently overwrite the whole-task
    // assignment for that slot. Callers are expected to refuse up front via
    // `hasSplitAssignments`; this keeps the function safe if one forgets.
    if (row.orderTaskCargoItemId != null) continue;
    kept.set(`${row.slot}:${row.slotIndex}`, toInput(row));
  }

  const applied: CardAssignment[] = [];
  const skipped: CardAssignment[] = [];

  for (const next of nexts) {
    const key = `${next.slot}:1`;
    if (next.onlyIfEmpty && kept.has(key)) {
      skipped.push(next);
      continue;
    }
    kept.set(key, {
      slot: next.slot,
      slotIndex: 1,
      isSubcontractor: next.isSubcontractor,
      resourceId: next.resourceId,
      subcontractorPartyId: next.subcontractorPartyId,
      subcontractorTrailerId: next.subcontractorTrailerId,
      externalReference: null,
      referenceSnapshot: next.referenceSnapshot,
    });
    applied.push(next);
  }

  return { items: [...kept.values()], applied, skipped };
}

/**
 * Resource / subcontractor-trailer id → the jobs it's currently on.
 *
 * <p>
 * Built from the queue rather than a new request: a movement already carries its
 * assignments and its `jobNo`, which is the order number for an order row and the
 * manifest number for a load row — exactly the two labels a card wants to show.
 * Orders that have been consolidated onto a load appear only as the load, so a
 * trailer on a manifest shows the manifest number rather than one of the orders
 * inside it.
 * </p>
 *
 * <p>
 * Pass the UNFILTERED movement list. A trailer is still busy when the job it's on
 * has been filtered out of view, and showing it as free would be a lie.
 * </p>
 */
export function buildResourceJobIndex(
  movements: readonly {
    jobNo: string;
    resources: readonly {
      slot: AssignmentSlotValue;
      resourceId?: string | null;
      subcontractorTrailerId?: string | null;
      subcontractorPartyId?: string | null;
    }[];
  }[],
): Map<string, string[]> {
  const index = new Map<string, string[]>();

  const add = (id: string | null | undefined, jobNo: string) => {
    if (!id) return;
    const jobs = index.get(id);
    // One resource can appear on several tasks of the same job; count it once.
    if (jobs) {
      if (!jobs.includes(jobNo)) jobs.push(jobNo);
    } else {
      index.set(id, [jobNo]);
    }
  };

  for (const movement of movements) {
    for (const row of movement.resources) {
      // Every id on the row is keyed, because each corresponds to a card the
      // planner might be looking at: our own driver / vehicle / trailer, a
      // registered subcontractor trailer, and the haulier supplying it — a
      // subcontracted slot is a commitment for that party too.
      add(row.resourceId, movement.jobNo);
      add(row.subcontractorTrailerId, movement.jobNo);
      add(row.subcontractorPartyId, movement.jobNo);
    }
  }

  return index;
}

/**
 * Fold assignments into ONE task's set, for a drop on a single stop.
 *
 * <p>
 * Distinct from {@link mergeAssignments}, which serves the order- and load-scoped
 * endpoints: those reject cargo-item references, so they drop split rows and
 * refuse outright when any exist. The per-task endpoint accepts them, so here they
 * are **carried through verbatim** — assigning a driver to a stop must not discard
 * a split allocation on that same stop.
 * </p>
 *
 * <p>
 * Only whole-task slot 1 is replaced. Higher slot indices (a drawbar's second
 * trailer) survive, and `onlyIfEmpty` entries — a driver's linked vehicle — skip
 * when something already holds the slot.
 * </p>
 */
export function mergeTaskAssignments(
  current: readonly ExistingAssignment[],
  nexts: readonly CardAssignment[],
): OrderTaskResourceInput[] {
  const { splits, whole } = partitionTaskRows(current);

  for (const next of nexts) {
    const key = `${next.slot}:1`;
    if (next.onlyIfEmpty && whole.has(key)) continue;
    whole.set(key, {
      slot: next.slot,
      slotIndex: 1,
      isSubcontractor: next.isSubcontractor,
      resourceId: next.resourceId,
      subcontractorPartyId: next.subcontractorPartyId,
      subcontractorTrailerId: next.subcontractorTrailerId,
      externalReference: null,
      referenceSnapshot: next.referenceSnapshot,
      orderTaskCargoItemId: null,
    });
  }

  return [...whole.values(), ...splits];
}

/**
 * Build the cascade payload: apply an assignment to the dropped stop and every one
 * after it.
 *
 * <p>
 * This is how a driver or vehicle handover part-way through a journey is
 * expressed — drop the relief driver on stop 3 and stops 3 onward change hands,
 * while 1 and 2 keep whoever had them. Earlier stops are simply not in the payload,
 * so they're untouched rather than rewritten with their existing values.
 * </p>
 *
 * <p>
 * Each stop is merged against its OWN current rows, so a downstream stop keeps its
 * own trailer and any split allocation — only the dropped slot changes. That's why
 * the batch endpoint takes per-task sets rather than one shared list.
 * </p>
 */
export function buildCascade(
  taskIds: readonly string[],
  fromIndex: number,
  currentByTaskId: (taskId: string) => readonly ExistingAssignment[],
  nexts: readonly CardAssignment[],
): { orderTaskId: string; items: OrderTaskResourceInput[] }[] {
  if (fromIndex < 0 || fromIndex >= taskIds.length) return [];
  return taskIds.slice(fromIndex).map((orderTaskId) => ({
    orderTaskId,
    items: mergeTaskAssignments(currentByTaskId(orderTaskId), nexts),
  }));
}

/**
 * Split one task's rows into the whole-task slots (keyed, so they can be replaced)
 * and the cargo-item-scoped ones (held aside verbatim).
 *
 * <p>
 * Shared by merge and remove so both treat splits the same way. Keying a split by
 * slot would let it overwrite the whole-task assignment for that slot — the bug
 * that had to be fixed in {@link mergeAssignments}.
 * </p>
 */
function partitionTaskRows(current: readonly ExistingAssignment[]): {
  splits: OrderTaskResourceInput[];
  whole: Map<string, OrderTaskResourceInput>;
} {
  const splits: OrderTaskResourceInput[] = [];
  const whole = new Map<string, OrderTaskResourceInput>();
  for (const row of current) {
    if (row.orderTaskCargoItemId != null) {
      splits.push({ ...toInput(row), orderTaskCargoItemId: row.orderTaskCargoItemId });
      continue;
    }
    whole.set(`${row.slot}:${row.slotIndex}`, toInput(row));
  }
  return { splits, whole };
}

/**
 * Clear one whole-task slot, leaving every other slot — and any cargo-item-scoped
 * rows — exactly as they were. Backs the × on a resource chip.
 */
export function removeTaskSlot(
  current: readonly ExistingAssignment[],
  slot: AssignmentSlotValue,
  slotIndex = 1,
): OrderTaskResourceInput[] {
  const { splits, whole } = partitionTaskRows(current);
  whole.delete(`${slot}:${slotIndex}`);
  return [...whole.values(), ...splits];
}
